// core
import { useState, useEffect, useRef } from "react";
import { Form, Link, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import classNames from "classnames";
import { toast } from "react-toastify";

// ui primitives (replaced @mui/material — see Components/UI)
import {
    Button,
    Card,
    CheckboxField,
    CircularProgress,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography,
    VisibilityIcon,
    VisibilityOffIcon,
} from "Components/UI";

// assets
import whiteLogo from "Assets/logo-rgb-white.svg";

import {
    faLock,
    faRightToBracket,
    faUser,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// services
import { useAuthenticate as authenticate } from "Services/Authentication";
import {
    forgetSamlAccount,
    isKnownSamlAccount,
    rememberSamlAccount,
} from "Utils/samlAccounts";
import {
    useLogin as login,
    useLoginGoogle as loginGoogle,
    onContinue,
} from "Services/LoginAPI";

// components
import BreakGlassRequestLink from "./BreakGlassRequestLink";
import LoginLogo from "./LoginLogo";
import OrSeparator from "./OrSeparator";
import SocialMedialLogins from "./SocialMedialLogins";

// styles
import "react-toastify/dist/ReactToastify.css";
import styles from "./LoginForm.module.css";

let isUnixiExtensionAllowed = false;
let unixiExtensionEnv = null;

const serverName = window.location.host;
const unixiExtensionExistsWithinThisPageAndReadytoGo = JSON.parse(
    localStorage.getItem(`${serverName}-managementPortalWithUnixi`),
);

let isUnixiExtensionActive = !!unixiExtensionExistsWithinThisPageAndReadytoGo;

isUnixiExtensionAllowed =
    !!unixiExtensionExistsWithinThisPageAndReadytoGo?.allowed;
unixiExtensionEnv = unixiExtensionExistsWithinThisPageAndReadytoGo?.env;

let managementPortalWithUnixi = {
    allowed: isUnixiExtensionAllowed,
    env: unixiExtensionEnv,
};

export default function LoginForm() {
    // injections
    const navigate = useNavigate();
    const { control, handleSubmit, getValues, watch } = useForm({
        defaultValues: {
            username: "",
        },
        mode: "onSubmit",
    });

    // own state

    const [extensionActive, setExtensionActive] = useState(
        isUnixiExtensionActive,
    );
    const [isPassword, setIsPassword] = useState(
        isUnixiExtensionActive && managementPortalWithUnixi.allowed,
    );
    const [loading, setLoading] = useState(false);
    const [rememberMe, setRememberMe] = useState(true);
    const [userCode, setUserCode] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [emergencyAccess, setEmergencyAccess] = useState(false);
    // Show "IdP not working?" for this attempt. Set when Continue resolves to
    // an IdP that offers emergency access, or when SAML is configured and
    // could not be initiated. Cleared for a password account, and for a
    // redirect that does not offer emergency access.
    const [samlContinue, setSamlContinue] = useState(false);
    // True from the moment Continue hands the browser to the IdP until that
    // navigation ends. While it is true this page is still on screen, and an IdP
    // that is not responding keeps it that way.
    const idpNavigationPendingRef = useRef(false);
    // The address whose Continue stopped short of the IdP to offer "IdP not
    // working?". The next Continue for that same address goes to the IdP.
    const emergencyHoldRef = useRef("");

    const isUnixiLogin = extensionActive && managementPortalWithUnixi.allowed;

    // "IdP not working?" is shown once Continue has resolved this address.
    // A redirect lights it only when login_sso sets emergency_access to true.
    // That first Continue stays on this page so the link can be clicked, and
    // the address is not stored. A 400 with password_login: false means
    // SAML is configured and could not start; that address is remembered for
    // this tab. A password account never sees the link, and neither does a
    // visitor who has not pressed Continue.
    //
    // This still discloses nothing about *which* account can bypass the IdP.
    // On a redirect it is every SAML address of a tenant login_sso marked
    // emergency_access, never one mailbox. The 400 answer does not carry that
    // field, so a SAML setup that cannot start still shows the link.
    //
    // Computed on every render rather than memoised: it reads sessionStorage,
    // which Continue mutates without changing either input. Keyed on
    // [samlContinue, typedUsername] a memo kept returning a stale `true` after
    // forgetSamlAccount() cleared an address, leaving the link offered to a
    // password account - the one case this change exists to prevent.
    const typedUsername = watch("username");
    const emergencyLinkVisible =
        samlContinue || isKnownSamlAccount(typedUsername);

    // private methods
    function completeLogin(response) {
        authenticate(response.user, response.tenant);
        const LandingPage = response.tenant.landing_page;

        completeRedirect(LandingPage);
    }

    function completeRedirect(LandingPage) {
        const redirectPath = localStorage.getItem("redirectPath") || "/";
        localStorage.removeItem("redirectPath"); // Clear the saved path
        if (redirectPath === "/") {
            window.location.href = LandingPage
                ? `/unixi-management-portal/app/${LandingPage}`
                : "/unixi-management-portal/app/dashboard";
        } else if (redirectPath.startsWith("/unixi-management-portal")) {
            window.location.href = `${redirectPath}`;
        } else {
            window.location.href = `/unixi-management-portal${redirectPath}`;
        }
    }

    function completeLoginGoogle(response) {
        var credential = response;
        loginGoogle(credential)
            .then((response) => {
                if (response && response.status) {
                    if (response.page === "login-mfa") {
                        navigate("login-mfa");
                    } else {
                        completeLogin(response);
                    }
                } else if (response) {
                    toast.error(response.info);
                } else {
                    toast.error("Communication error occurred.");
                }
            })
            .catch((error) => {
                toast.error(error.toString());
                console.log(error);
            });
    }

    function doLogin(withUnixi = false) {
        return function (formValues) {
            setLoading(true);

            // Call useLogin and then based on the response, set the rememberedLogin flag
            login(
                formValues.username,
                formValues.password,
                withUnixi,
                userCode,
                rememberMe && userCode != null,
            )
                .then((response) => {
                    if (response && response.status) {
                        if (rememberMe && userCode != null) {
                            window.postMessage(
                                {
                                    type: `${managementPortalWithUnixi.env}-SUCCESSFUL_REMEMBER_ME_LOGIN`,
                                    management_portal_with_unixi: {
                                        allowed: true,
                                        env: managementPortalWithUnixi.env,
                                    },
                                },
                                "*",
                            );
                        }
                        localStorage.setItem(
                            `${serverName}-managementPortalWithUnixi`,
                            JSON.stringify({
                                allowed:
                                    managementPortalWithUnixi.allowed ||
                                    (rememberMe && userCode != null),
                                env: managementPortalWithUnixi.env,
                            }),
                        );

                        localStorage.setItem(
                            "first_login",
                            JSON.stringify(true),
                        );

                        if (response.page === "login-mfa") {
                            // localStorage.setItem(
                            //     'fromLocation',
                            //     props.fromLocation || '/app/dashboard'
                            // );
                            navigate("login-mfa");
                        } else {
                            completeLogin(response);
                        }
                    } else if (response) {
                        toast.error(response.info);
                        if (isUnixiLogin && !userCode) {
                            localStorage.removeItem(
                                `${serverName}-managementPortalWithUnixi`,
                            );
                            setExtensionActive(false);
                            managementPortalWithUnixi.allowed = false;
                        }
                    } else {
                        toast.error("Communication error occurred.");
                    }
                    setLoading(false);
                })
                .catch((error) => {
                    toast.error(error.toString());
                    console.log(error);
                    setLoading(false);
                })
                .finally(() => {
                    setLoading(false);
                });
        };
    }

    async function handleContinue() {
        setLoading(true);

        const username = getValues("username");
        if (!username) {
            toast.error("Email is required");
            setLoading(false);
            return;
        }

        try {
            const response = await onContinue(username);

            if (response) {
                if (response?.status) {
                    if (response?.redirect_url) {
                        // Strict true: a missing field is an older backend or
                        // an error body, and neither is an offer of emergency
                        // access.
                        const offersEmergencyAccess =
                            response?.emergency_access === true;
                        if (offersEmergencyAccess) {
                            setSamlContinue(true);
                        } else {
                            forgetSamlAccount(username);
                            setSamlContinue(false);
                        }

                        // A responsive IdP replaces this page within a
                        // fraction of a second, too fast to click the link.
                        // So the first Continue for an address that offers
                        // emergency access stops here, and the next one
                        // leaves, with a fresh login_sso redirect. The
                        // address is not remembered: there is no way back
                        // from the provider to dismiss the link.
                        if (
                            offersEmergencyAccess &&
                            emergencyHoldRef.current !== username
                        ) {
                            emergencyHoldRef.current = username;
                        } else {
                            localStorage.setItem(
                                "first_login",
                                JSON.stringify(true),
                            );
                            idpNavigationPendingRef.current = true;
                            window.location.href = response.redirect_url;
                        }
                    } else if (response?.password_login) {
                        // A password account: no identity provider stands
                        // between this admin and the console, so emergency
                        // access does not apply. Drop any stale memory of this
                        // address too, or the link would keep reappearing for a
                        // tenant that has since moved off SAML.
                        forgetSamlAccount(username);
                        setSamlContinue(false);
                        setIsPassword(true);
                    }
                } else {
                    // A 400 carrying password_login:false is the one failure
                    // that means SAML *is* configured for the tenant and could
                    // not be initiated - the outage emergency access exists for.
                    // The other 400s (missing or malformed email) omit the key
                    // entirely and the 500 arrives with a different status, so
                    // neither lights the link.
                    //
                    // We honour password_login:false and leave the password
                    // field hidden: the backend is saying there is no password
                    // to offer here.
                    if (
                        response.httpStatus === 400 &&
                        response.password_login === false
                    ) {
                        rememberSamlAccount(username);
                        setSamlContinue(true);
                    }

                    toast.error(
                        response.message ||
                            "Could not sign you in. Please try again.",
                    );
                }
            }

            setLoading(false);
        } catch (error) {
            setLoading(false);
            if (error?.message) {
                toast.error(error.message);
            }
        }
    }

    function handleFormSubmit(e) {
        // Make "Enter" behave like clicking the primary CTA in the current step.
        // We prevent the native/React-Router form submission since we handle login client-side.
        e?.preventDefault?.();

        if (!isPassword) {
            handleSubmit(handleContinue)();
            return;
        }

        handleSubmit(doLogin(isUnixiLogin))();
    }

    function handleMicrosoftLogin() {
        const tenantId = "organizations";
        const clientId = "a3330774-170c-41b9-b3b1-a33a00c2146c";
        // Build the redirect URI including the parent's origin as a query param
        // Create a state object that includes your frontend's origin
        const stateObj = { frontend_origin: window.location.origin };
        // Encode it as a JSON string (and then URI-encode it)
        const state = encodeURIComponent(JSON.stringify(stateObj));

        // Generate a random nonce for security
        const nonce =
            Math.random().toString(36).substring(2, 15) +
            Math.random().toString(36).substring(2, 15);

        // Use your registered redirect URI exactly as is:
        const redirectUri = `${window.location.protocol}//${serverName}/api/authentication/azure_callback`;
        const authUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?client_id=${clientId}&response_type=id_token&redirect_uri=${encodeURIComponent(
            redirectUri,
        )}&scope=openid%20email%20profile&state=${state}&nonce=${nonce}&response_mode=form_post`;

        window.open(authUrl, "mslogin", "width=600,height=700");

        // Listen for the message from the popup
        const handleMicrosoftMessage = async (event) => {
            if (event.origin !== window.location.origin) return;

            if (event.data && event.data.success === "true") {
                let userObj = event.data.user;
                let tenantObj = event.data.tenant;
                const response = {
                    status: true,
                    user: userObj,
                    tenant: tenantObj,
                    page: tenantObj.landing_page,
                };
                cleanup({ keepLoading: true });
                completeLogin(response);
            } else if (event.data && event.data.success === "false") {
                toast.error(
                    `ERROR: ${event.data.info || "Microsoft login failed"}`,
                );
                cleanup();
            }
        };

        window.addEventListener("message", handleMicrosoftMessage);

        const intervalId = setInterval(() => {
            // Are there any user&tenant details in the localSession & localStorage
            if (localStorage.getItem("tenant")) {
                let response = {
                    status: true,
                    tenant: JSON.parse(localStorage.getItem("tenant")),
                    page: JSON.parse(localStorage.getItem("tenant"))
                        .landing_page,
                };
                cleanup({ keepLoading: true });
                completeLogin(response);
            }
        }, 1000);

        const cleanup = ({ keepLoading } = {}) => {
            window.removeEventListener("message", handleMicrosoftMessage);
            clearInterval(intervalId);
        };

        // Note: In a React event handler, the cleanup function returned here is not used.
        // You may want to move the event listener registration into a useEffect instead.
    }

    function openEmergencyAccess() {
        // An IdP that is not responding leaves its navigation pending with this
        // page still showing. Cancel it, or it completes - or fails into a
        // browser error page - while the admin is typing into the emergency form.
        if (idpNavigationPendingRef.current) {
            idpNavigationPendingRef.current = false;
            window.stop();
        }
        setEmergencyAccess(true);
    }

    // Conditionally render the login form based on extensionActive and managementPortalWithUnixi state

    useEffect(() => {
        // A back-forward cache restore keeps this page's state, including the
        // pending flag, but the navigation it describes is long over.
        const handlePageShow = (event) => {
            if (event.persisted) {
                idpNavigationPendingRef.current = false;
                setLoading(false);
            }
        };
        window.addEventListener("pageshow", handlePageShow);

        return () => {
            window.removeEventListener("pageshow", handlePageShow);
        };
    }, []);

    useEffect(() => {
        // Send message to extension to get user code
        window.postMessage({ type: `${serverName}-GET_USER_DATA` }, "*");

        // Listen for the response from the content script
        const handleExtensionMessage = (event) => {
            if (
                event.data &&
                event.data.type === `${serverName}-USER_DATA_RESPONSE`
            ) {
                const user_code = event.data.user_code;
                setUserCode(user_code);
            }
        };

        window.addEventListener("message", handleExtensionMessage);

        // Cleanup the event listener on component unmount
        return () => {
            window.removeEventListener("message", handleExtensionMessage);
        };
    }, []);

    if (emergencyAccess) {
        return (
            <BreakGlassRequestLink
                initialEmail={getValues("username") || ""}
                onCancel={() => setEmergencyAccess(false)}
            />
        );
    }

    return (
        <Card flat>
            <Stack fullWidth gap={3}>
                <Stack align="center" direction="row" justify="center">
                    <LoginLogo />
                </Stack>
                <Stack align="center" direction="row" justify="center">
                    <Typography variant="h6">Please Sign In</Typography>
                </Stack>

                <Form onSubmit={handleFormSubmit}>
                    {/* Conditionally render username and password fields only if Unixi is not enabled */}
                    {!extensionActive || !managementPortalWithUnixi.allowed ? (
                        <Stack gap={3}>
                            {/* The email field and its "IdP not working?" link are one
                                block, so the stack gap does not push the link away from
                                the field it belongs to. */}
                            <div>
                                <Controller
                                    defaultValue=""
                                    control={control}
                                    name="username"
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            error={!!fieldState.error}
                                            helperText={
                                                !!fieldState.error &&
                                                "Email is required"
                                            }
                                            fullWidth
                                            disabled={loading}
                                            inputProps={{
                                                "data-testid": "username",
                                            }}
                                            onChange={(e) => {
                                                field.onChange(e);
                                                // An edited address has not been
                                                // resolved by Continue yet.
                                                setSamlContinue(false);
                                                emergencyHoldRef.current = "";
                                            }}
                                            label={
                                                <Stack
                                                    align="center"
                                                    direction="row"
                                                    gap={1}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faUser}
                                                    />
                                                    <Typography variant="body1">
                                                        Email
                                                    </Typography>
                                                </Stack>
                                            }
                                            value={field.value}
                                        />
                                    )}
                                    rules={{ required: true }}
                                />

                                {emergencyLinkVisible ? (
                                    <div className={styles.emergencyBlock}>
                                        <button
                                            type="button"
                                            data-testid="idp-not-working"
                                            className={classNames(
                                                styles.link,
                                                styles.linkThemed,
                                                {
                                                    [styles.linkDisabled]: loading,
                                                },
                                            )}
                                            disabled={loading}
                                            onClick={openEmergencyAccess}
                                        >
                                            IdP not working?
                                        </button>
                                    </div>
                                ) : null}
                            </div>

                            {isPassword && (
                                <Stack>
                                    <Controller
                                        defaultValue=""
                                        control={control}
                                        name="password"
                                        render={({ field, fieldState }) => (
                                            <TextField
                                                error={!!fieldState.error}
                                                helperText={
                                                    !!fieldState.error &&
                                                    "Password is required"
                                                }
                                                autoFocus
                                                fullWidth
                                                disabled={loading}
                                                endAdornment={
                                                    <IconButton
                                                        edgeEnd
                                                        aria-label={
                                                            showPassword
                                                                ? "Hide password"
                                                                : "Show password"
                                                        }
                                                        data-testid="toggle-password-visibility"
                                                        disabled={loading}
                                                        onClick={() =>
                                                            setShowPassword(
                                                                (prev) => !prev,
                                                            )
                                                        }
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                        }}
                                                    >
                                                        {showPassword ? (
                                                            <VisibilityOffIcon />
                                                        ) : (
                                                            <VisibilityIcon />
                                                        )}
                                                    </IconButton>
                                                }
                                                inputProps={{
                                                    "data-testid": "password",
                                                }}
                                                onChange={(e) => {
                                                    field.onChange(e);
                                                }}
                                                label={
                                                    <Stack
                                                        align="center"
                                                        direction="row"
                                                        gap={1}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={faLock}
                                                        />
                                                        <Typography variant="body1">
                                                            Password
                                                        </Typography>
                                                    </Stack>
                                                }
                                                type={
                                                    showPassword
                                                        ? "text"
                                                        : "password"
                                                }
                                                value={field.value}
                                            />
                                        )}
                                        rules={{ required: isPassword }}
                                    />

                                    {!managementPortalWithUnixi.allowed && (
                                        <div className={styles.rememberRow}>
                                            <Tooltip
                                                title={
                                                    !userCode
                                                        ? "Remember Me is only available with Unixi extension"
                                                        : "This will allow you to login faster while using Unixi extension"
                                                }
                                            >
                                                <CheckboxField
                                                    checked={
                                                        rememberMe &&
                                                        userCode != null
                                                    }
                                                    disabled={
                                                        !userCode || loading
                                                    }
                                                    label="Remember Me"
                                                    onChange={(e) =>
                                                        setRememberMe(
                                                            e.target.checked,
                                                        )
                                                    }
                                                />
                                            </Tooltip>

                                            <Link
                                                to="/forgot-password"
                                                state={{
                                                    username:
                                                        getValues("username") ||
                                                        "",
                                                }}
                                                className={classNames(
                                                    styles.link,
                                                    styles.linkThemed,
                                                    {
                                                        [styles.linkDisabled]:
                                                            loading,
                                                    },
                                                )}
                                                aria-disabled={loading}
                                                tabIndex={loading ? -1 : 0}
                                                onClick={(e) => {
                                                    if (!loading) return;
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                }}
                                            >
                                                Forgot Password?
                                            </Link>
                                        </div>
                                    )}
                                </Stack>
                            )}
                        </Stack>
                    ) : null}

                    {isPassword ? (
                        <div className={styles.submitBlock}>
                            {extensionActive &&
                            managementPortalWithUnixi.allowed ? (
                                <Button
                                    type="submit"
                                    fullWidth
                                    disabled={loading}
                                    endIcon={
                                        loading ? (
                                            <CircularProgress size={18} />
                                        ) : (
                                            <img
                                                src={whiteLogo}
                                                className={styles.logoButton}
                                                alt="Unixi Logo"
                                            />
                                        )
                                    }
                                >
                                    Login with
                                </Button>
                            ) : (
                                <Button
                                    type="submit"
                                    data-testid="sign-in-button"
                                    fullWidth
                                    disabled={loading}
                                    endIcon={
                                        loading ? (
                                            <CircularProgress size={18} />
                                        ) : (
                                            <FontAwesomeIcon
                                                icon={faRightToBracket}
                                            />
                                        )
                                    }
                                >
                                    Sign In
                                </Button>
                            )}
                        </div>
                    ) : (
                        <div className={styles.submitBlock}>
                            <Button
                                data-testid="continue-button"
                                type="submit"
                                fullWidth
                                disabled={loading}
                                endIcon={
                                    loading ? (
                                        <CircularProgress size={18} />
                                    ) : (
                                        <FontAwesomeIcon
                                            icon={faRightToBracket}
                                        />
                                    )
                                }
                            >
                                Continue
                            </Button>
                        </div>
                    )}

                    <Stack gap={2} mt={2}>
                        <OrSeparator />

                        <SocialMedialLogins
                            loading={loading}
                            setLoading={setLoading}
                            onGoogleSuccess={completeLoginGoogle}
                            onMicrosoftClick={handleMicrosoftLogin}
                        />
                    </Stack>
                </Form>
            </Stack>

            {extensionActive && managementPortalWithUnixi.allowed ? (
                <Stack
                    align="center"
                    className={styles.otherMethods}
                    direction="row"
                    justify="center"
                    mt={2}
                    onClick={() => {
                        setExtensionActive(false);
                        setRememberMe(false);
                    }}
                >
                    Login using other methods
                </Stack>
            ) : null}
        </Card>
    );
}
